import { useState } from "react";
import { Typography, Box } from "@mui/material";
import MzDataGrid from "@markaaz/ui/MzDataGrid";
import MzButton from "@markaaz/ui/MzButton";
import { MzDialog, MzDialogContent, MzDialogActions } from "@markaaz/ui/MzDialogControls";
import dayjs from "dayjs";
import { ReactComponent as CheckCircle } from "assets/icons/check-circle.svg";

const sx = {
  datagrid: {
    maxHeight: "806px",
    maxWidth: "860px",
    "& .MuiDataGrid-columnHeader": {
      paddingLeft: "20px"
    },
    "& .MuiDataGrid-columnHeaders": {
      borderBottom: 0
    },
    "& .MuiDataGrid-columnHeaderTitle": (theme) => {
      return { ...theme.typography.body2M };
    },
    "& .MuiDataGrid-cell": {
      paddingLeft: "20px"
    },
    "& .MuiDataGrid-cell:last-child, & .MuiDataGrid-cell:nth-last-child(2)": {
      padding: 0
    },
    "& .MuiDataGrid-row:last-child .MuiDataGrid-cell": {
      borderBottom: "none"
    },
    "& .MuiDataGrid-row:last-child": {
      borderBottomLeftRadius: "8px"
    },
    "& .MuiDataGrid-virtualScroller": {
      overflowY: "auto",
      "&::-webkit-scrollbar": {
        backgroundColor: (theme) => `${theme.palette.common.white}`,
        borderRadius: "0px 0px 8px 0px"
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: (theme) => `${theme.palette.darkBlue.c20}`
      }
    }
  },

  confirmText: {
    color: "darkBlue.c100",
    width: "350px",
    textAlign: "center"
  }
};

export default function UserGrid({
  loading,
  data,
  openInviteSentForm,
  onEdit,
  onDelete,
  onResend,
  onInviteSentFormClose
}) {
  const [selectedUser, setSelectedUser] = useState(null);
  const [open, setOpen] = useState(false);

  const columns = [
    {
      field: "firstName",
      headerName: "User",
      width: 360,
      sortable: true,
      renderCell: ({ row, value }) => {
        return (
          <Box className="MuiDataGrid-cellContent">
            <Typography variant="body1M">{`${row.firstName} ${row.lastName}`}</Typography>
            <Typography variant="body2">{row.email}</Typography>
          </Box>
        );
      }
    },
    {
      field: "userRole",
      headerName: "Role",
      width: 140,
      sortable: true
    },
    {
      field: "status",
      headerName: "Status",
      width: 160,
      sortable: true,
      renderCell: ({ row, value }) => {
        return (
          <Box className="MuiDataGrid-cellContent">
            {value}
            {value === "Invited" && (
              <>
                <Box display="inline">&nbsp;({dayjs(row.invitedDate).format("MM/DD/YY")})</Box>
                <br />
                <MzButton
                  sx={{ px: 0 }}
                  onClick={() => {
                    onResend(row);
                  }}
                >
                  Resend Verification
                </MzButton>
              </>
            )}
          </Box>
        );
      }
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 160,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Box className="MuiDataGrid-cellContent">
            <MzButton
              onClick={() => {
                onEdit(row);
              }}
            >
              Edit
            </MzButton>
            <MzButton color="error" onClick={handleRemove(row)}>
              Remove
            </MzButton>
          </Box>
        );
      }
    }
  ];

  function handleRemove(row) {
    return function () {
      setSelectedUser(row);
      setOpen(true);
    };
  }

  function handleClose() {
    setOpen(false);
  }

  function handleConfirmRemove() {
    onDelete(selectedUser);
    handleClose();
  }

  const rowHeight = 75;
  const columnHeaderHeight = 56;

  return (
    <>
      <MzDataGrid
        rowHeight={rowHeight}
        columnHeaderHeight={columnHeaderHeight}
        sx={sx.datagrid}
        getRowId={(row) => row.epPortalUserId}
        loading={loading}
        variant="3"
        rows={data}
        columns={columns}
        disableSelectionOnClick
        disableColumnMenu={true}
        hideFooterPagination
        hideFooter
      />
      <MzDialog open={open} onClose={handleClose}>
        <MzDialogContent>
          <Typography variant="h5" sx={sx.confirmText}>
            Do you want to proceed with removing this user?
          </Typography>
        </MzDialogContent>
        <MzDialogActions>
          <MzButton variant="outlined" color="secondary" size="large" onClick={handleClose}>
            Cancel
          </MzButton>
          <MzButton variant="contained" color="error" size="large" onClick={handleConfirmRemove}>
            Remove
          </MzButton>
        </MzDialogActions>
      </MzDialog>

      <MzDialog open={openInviteSentForm} onClose={handleClose}>
        <MzDialogContent
          sx={{
            padding: "0px 148px 20px 148px"
          }}
        >
          <CheckCircle />
          <Typography variant="h5" py="50px">
            Invitation Sent!
          </Typography>
          <MzButton variant="contained" size="large" onClick={onInviteSentFormClose}>
            Close
          </MzButton>
        </MzDialogContent>
      </MzDialog>
    </>
  );
}
